/**
 * Isolates notary vs client session cookies/stores when SPA navigation crosses route families.
 */

import { useEffect, useRef } from "react";
import { useRouter } from "next/router";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import { isolateSessionContextForRoute } from "@Front/Hooks/helpers/bootstrapSessionContextIsolation";
import { resolveRouteContextFamily, type RouteContextFamily } from "@Front/Utils/routeContextRegistry";

function extractPathFromRouteUrl(url: string): string {
	return url.split("?")[0] || "";
}

export function useSessionContextRouteIsolation(): void {
	const router = useRouter();
	const lastFamilyRef = useRef<RouteContextFamily>("neutral");

	useEffect(() => {
		if (!router.isReady) {
			return;
		}
		lastFamilyRef.current = resolveRouteContextFamily(router.pathname);
	}, [router.isReady, router.pathname]);

	useEffect(() => {
		const handleRouteChangeStart = (url: string): void => {
			const pathname = extractPathFromRouteUrl(url);
			const nextFamily = resolveRouteContextFamily(pathname);
			const previousFamily = lastFamilyRef.current;
			if (nextFamily === previousFamily || nextFamily === "neutral") {
				lastFamilyRef.current = nextFamily === "neutral" ? previousFamily : nextFamily;
				return;
			}
			void isolateSessionContextForRoute(pathname, "navigation")
				.then(() => {
					lastFamilyRef.current = nextFamily;
				})
				.catch((error: unknown) => {
					LoggerService.error("❌ [SessionContextGuard] Failed to isolate session on navigation", {
						error,
						pathname,
						previousFamily,
						nextFamily,
					});
				});
		};

		router.events.on("routeChangeStart", handleRouteChangeStart);
		return (): void => {
			router.events.off("routeChangeStart", handleRouteChangeStart);
		};
	}, [router.events]);
}
